import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, AlertCircle, CheckCircle2, Lock, Shield, ExternalLink, ChevronDown,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Spinner, Pill } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { PdfReader, fetchPdfBytes } from "@/components/pdf/PdfReader";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// PolicyReaderScreen — /policies/:policyCode
// T-121 phase 1 (B2 + B3) — read the policy, then acknowledge it via the
// app.record_policy_ack RPC (tick + click is the sign; audit metadata is the
// proof: viewed_at, scrolled_to_end_at, signed_at, user_agent).
//
// Rebuilt 3 Oct 2026. James: "the current policy reader I'm not happy with —
// either click to go to a PDF tab then the user has to go back on the web
// browser to get back to acknowledge, or you view in the app and it's not
// sized right and scrolls weird, so try improve both please for user
// experience." Both were the <iframe>. Now:
//   * The PDF is DRAWN INTO THE PAGE (components/pdf/PdfReader — pdf.js),
//     every page the width of the screen, stacked, scrolled like any other
//     screen. Rotate the phone and it redraws. No new tab, no inner scroll.
//   * The acknowledge bar is STICKY at the bottom: page x of n while reading,
//     then the tick + Acknowledge the moment the last page is on screen. The
//     person never leaves the document to sign it.
//   * scrolled_to_end_at is now REAL — stamped when the last page scrolls into
//     view (the iframe could never tell us; we used a 15 s dwell as a proxy).
//     The 15 s floor stays as a second gate so a one-page policy can't be
//     acknowledged in the time it takes to find the button.
//   * "Open in a new tab" remains, small, as a fallback — and if pdf.js can't
//     draw the file for any reason the screen falls back to the old dwell gate
//     so nobody is ever locked out of acknowledging.
// Bytes come through the api-gateway (/files/:id/pdf-bytes, JWT + RLS) rather
// than a signed R2 URL, so there is no R2 CORS and nothing expires mid-read.
//
// Three states: already current · just acknowledged · pending (read + sign).
// =============================================================================

interface PolicyDetail {
  policy_id:     string;
  policy_code:   string;
  display_name:  string;
  policy_category: string | null;
  document_id:   string;
}
interface FileDetail {
  file_id:           string;
  version_label:     string | null;
  is_major_revision: boolean;
  pdf_object_key:    string | null;
}
type AckStatus =
  | "current"
  | "never_signed"
  | "expired_annual"
  | "major_revision_pending";
interface AckSnapshot {
  ack_status:           AckStatus;
  last_ack_at:          string | null;
  last_ack_expires_at:  string | null;
}

const MIN_DWELL_SECONDS = 15;

// -----------------------------------------------------------------------------

export default function PolicyReaderScreen() {
  const { policyCode } = useParams<{ policyCode: string }>();
  const user           = useRequireAuth();
  const navigate       = useNavigate();
  const qc             = useQueryClient();

  const [tickedReviewed, setTickedReviewed] = useState(false);
  const [secondsRead,    setSecondsRead]    = useState(0);
  const [pdfBytes,       setPdfBytes]       = useState<ArrayBuffer | null>(null);
  const [pdfError,       setPdfError]       = useState<string | null>(null);
  const [readerBroken,   setReaderBroken]   = useState(false);   // pdf.js couldn't draw it → dwell-only fallback
  const [reachedEnd,     setReachedEnd]     = useState(false);
  const [progress,       setProgress]       = useState<{ page: number; count: number } | null>(null);
  const [externalUrl,    setExternalUrl]    = useState<string | null>(null);
  const [acking,         setAcking]         = useState(false);
  const [ackError,       setAckError]       = useState<string | null>(null);
  const [justAcked,      setJustAcked]      = useState<{ signed_at: string; expires_at: string } | null>(null);

  const viewedAtRef        = useRef<Date>(new Date());
  const scrolledToEndAtRef = useRef<Date | null>(null);

  // 1. Policy + current file + the user's ack status.
  const { data, isLoading, error } = useQuery({
    queryKey: ["policy_reader", policyCode, user?.user_id],
    enabled:  !!policyCode && !!user?.user_id,
    queryFn:  async () => {
      const { data: pol, error: e1 } = await supabase
        .from("policies")
        .select(`policy_id, policy_code, display_name, policy_category, document_id`)
        .eq("policy_code", policyCode!)
        .maybeSingle();
      if (e1) throw e1;
      if (!pol) throw new Error("policy_not_found");

      const { data: file, error: e2 } = await supabase
        .from("files")
        .select("file_id, version_label, is_major_revision, pdf_object_key")
        .eq("document_id", (pol as any).document_id)
        .eq("is_current_version", true)
        .maybeSingle();
      if (e2) throw e2;
      if (!file) throw new Error("no_current_file");

      // The pending-view returns a row per (user, applicable policy) — even
      // 'current' rows — so exactly one row should come back here.
      const { data: ack, error: e3 } = await supabase
        .from("v_policy_pending_for_user")
        .select("ack_status, last_ack_at, last_ack_expires_at")
        .eq("user_id",   user!.user_id)
        .eq("policy_id", (pol as any).policy_id)
        .maybeSingle();
      if (e3) throw e3;

      const ackSnap: AckSnapshot = (ack as any) ?? {
        ack_status:           "never_signed" as AckStatus,
        last_ack_at:          null,
        last_ack_expires_at:  null,
      };
      return {
        policy: pol  as unknown as PolicyDetail,
        file:   file as unknown as FileDetail,
        ack:    ackSnap,
      };
    },
  });

  // 2. The PDF bytes, through the gateway (JWT; the files RLS decides).
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!data?.file?.file_id) return;
      if (!data.file.pdf_object_key) {
        setPdfError("This policy hasn't had its PDF version uploaded yet. Talk to a manager.");
        return;
      }
      try {
        const bytes = await fetchPdfBytes(apiFetch, data.file.file_id);
        if (!cancelled) setPdfBytes(bytes);
      } catch (e: any) {
        if (!cancelled) setPdfError(e?.message ?? String(e));
      }
    }
    run();
    return () => { cancelled = true; };
  }, [data?.file?.file_id, data?.file?.pdf_object_key]);

  // 2b. A signed URL for "Open in a new tab" — fetched lazily on first click.
  async function openExternal() {
    if (!data?.file?.file_id) return;
    try {
      let url = externalUrl;
      if (!url) {
        const r = await apiFetch("/policies/sign-pdf", { method: "POST", body: JSON.stringify({ file_id: data.file.file_id }) });
        const j = await r.json().catch(() => ({} as any));
        url = (j as any).pdf_url ?? null;
        if (url) setExternalUrl(url);
      }
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch { /* the inline reader is the primary path; a failed fallback is not fatal */ }
  }

  // 3. Dwell timer — the 15 s floor. Starts once the document is on screen.
  useEffect(() => {
    if (!pdfBytes && !pdfError) return;
    const id = setInterval(() => setSecondsRead(s => Math.min(s + 1, MIN_DWELL_SECONDS)), 1000);
    return () => clearInterval(id);
  }, [pdfBytes, pdfError]);

  const dwellSatisfied = secondsRead >= MIN_DWELL_SECONDS;
  // The real gate is reaching the last page. If pdf.js couldn't draw the file
  // (or there is no PDF to draw) the old dwell-only gate applies, so a broken
  // renderer never blocks a person from acknowledging.
  const readGate       = readerBroken || !!pdfError ? dwellSatisfied : (reachedEnd && dwellSatisfied);
  const canAcknowledge = readGate && tickedReviewed && !acking;

  function onReachedEnd() {
    setReachedEnd(true);
    if (scrolledToEndAtRef.current === null) scrolledToEndAtRef.current = new Date();
  }

  // 4. Submit — record_policy_ack RPC, then refresh the lists that show ack state.
  async function submitAck() {
    if (!canAcknowledge || !data) return;
    setAcking(true);
    setAckError(null);
    try {
      // Dwell-only fallback: stamp scrolled_to_end_at at the dwell moment, as before.
      if (scrolledToEndAtRef.current === null && dwellSatisfied) scrolledToEndAtRef.current = new Date();
      const { error: rpcError } = await supabase.schema("app").rpc("record_policy_ack", {
        p_policy_id:          data.policy.policy_id,
        p_document_file_id:   data.file.file_id,
        p_signature_method:   "typed",   // tickbox treated as typed-style confirmation
        p_signature_svg:      undefined,
        p_signature_file_id:  undefined,
        p_viewed_at:          viewedAtRef.current.toISOString(),
        p_scrolled_to_end_at: scrolledToEndAtRef.current?.toISOString() ?? undefined,
        p_ip_address:         undefined,
        p_user_agent:         (typeof navigator !== "undefined" ? navigator.userAgent : undefined),
      });
      if (rpcError) throw rpcError;

      qc.invalidateQueries({ queryKey: ["my_policies"] });
      qc.invalidateQueries({ queryKey: ["hr_policy_admin"] });
      qc.invalidateQueries({ queryKey: ["my_policies_due"] });
      qc.invalidateQueries({ queryKey: ["policy_reader", policyCode] });

      const signed_at  = new Date().toISOString();
      const expires_at = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      setJustAcked({ signed_at, expires_at });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setAckError(e?.message ?? String(e));
    } finally {
      setAcking(false);
    }
  }

  // -------------------------------------------------------------------------
  if (isLoading) {
    return <div className="py-20 flex justify-center"><Spinner /></div>;
  }
  if (error || !data) {
    return (
      <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
        <BackLink/>
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Couldn't load policy <span className="font-mono">{policyCode}</span>:{" "}
            {String((error as any)?.message ?? error ?? "not found")}.
          </div>
        </Card>
      </div>
    );
  }

  const { policy, file, ack } = data;

  // State 1 — already acknowledged this version, ack still valid.
  if (ack.ack_status === "current" && !justAcked) {
    return <AlreadyAckedView policy={policy} file={file} ack={ack}/>;
  }

  // State 2 — just acknowledged in this session.
  if (justAcked) {
    return (
      <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
        <BackLink/>
        <header className="mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{policy.policy_code}</span>
            {file.version_label && <span className="font-mono text-[12px] text-text-dim">{file.version_label}</span>}
            <Pill label="acknowledged" tone="ok" />
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{policy.display_name}</h1>
        </header>
        <Card>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-ok">
              <CheckCircle2 size={20}/>
              <span className="font-semibold">Acknowledgement recorded.</span>
            </div>
            <div className="text-[13px] text-text-muted">
              Signed at <strong className="text-text">{fmtDate(justAcked.signed_at)}</strong> ·
              valid until <strong className="text-text">{fmtDate(justAcked.expires_at)}</strong>.
              You'll be asked to re-acknowledge if the policy is revised or every 12 months,
              whichever comes first.
            </div>
            <div className="flex justify-end">
              <Button onClick={() => navigate("/policies")}>Back to My Policies</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // State 3 — pending: read, then sign from the sticky bar.
  const pageLabel = progress ? `Page ${progress.page} of ${progress.count}` : null;

  return (
    <div className="p-3 md:p-6 max-w-[1100px] mx-auto">
      <BackLink/>

      {/* Title strip */}
      <header className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[13px] text-accent">{policy.policy_code}</span>
          {file.version_label && (
            <span className="font-mono text-[12px] text-text-dim">{file.version_label}</span>
          )}
          {file.is_major_revision && <Pill label="major revision" tone="warning" />}
          {ack.ack_status === "expired_annual" && <Pill label="annual due" tone="warning" />}
          {ack.ack_status === "major_revision_pending" && <Pill label="re-ack needed" tone="warning" />}
        </div>
        <h1 className="text-[22px] font-semibold tracking-tight mt-1">{policy.display_name}</h1>
        <div className="text-text-muted text-[13px] mt-1 flex items-center justify-between gap-3 flex-wrap">
          <span>{policy.policy_category ?? "—"} · read to the end, then confirm in the bar below.</span>
          {!pdfError && (
            <button type="button" onClick={openExternal} className="text-[12px] text-text-muted hover:text-accent inline-flex items-center gap-1">
              <ExternalLink size={12}/> Open in a new tab
            </button>
          )}
        </div>
      </header>

      {/* The document, drawn into the page */}
      {pdfError ? (
        <Card>
          <div className="p-6 text-text-muted text-sm">
            <AlertCircle size={16} className="inline mr-2 text-lost"/>
            Couldn't load the PDF: <span className="font-mono">{pdfError}</span>
          </div>
        </Card>
      ) : (
        <div className="rounded-xl bg-ink-raised/60 border border-line px-2 md:px-4">
          <PdfReader
            data={pdfBytes}
            onReachedEnd={onReachedEnd}
            onProgress={(page, count) => setProgress({ page, count })}
          />
          <ReaderHealth bytes={pdfBytes} onBroken={() => setReaderBroken(true)} />
        </div>
      )}

      {/* The bottom-of-document line: the person knows they are done. */}
      {reachedEnd && !pdfError && (
        <div className="mt-3 text-[12px] text-ok flex items-center gap-1.5"><CheckCircle2 size={14}/> You've reached the end of the document.</div>
      )}
      {ackError && (
        <div className="mt-3 text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">
          Couldn't record your acknowledgement: {ackError}
        </div>
      )}

      <div className="mt-3 mb-2 text-[11px] text-text-dim">
        Audit metadata captured: viewed-at, scrolled-to-end-at, signed-at, user-agent. The acknowledgement
        record is immutable — even directors can't edit or delete it.
      </div>

      {/* Sticky sign bar — never leave the document to acknowledge it */}
      <StickyBottomBar className="px-3 py-2.5">
        <div className="max-w-[1100px] mx-auto flex items-center gap-3 flex-wrap">
          {!readGate ? (
            <div className="flex-1 min-w-[180px] text-[12px] text-text-muted flex items-center gap-2">
              <ChevronDown size={14} className="animate-bounce text-accent shrink-0"/>
              <span>
                {pageLabel ? `${pageLabel} — ` : ""}
                {readerBroken || pdfError
                  ? `Reading time ${secondsRead}s of ${MIN_DWELL_SECONDS}s`
                  : !reachedEnd ? "scroll to the end to acknowledge"
                  : `a moment… (${secondsRead}s of ${MIN_DWELL_SECONDS}s)`}
              </span>
            </div>
          ) : (
            <label className="flex-1 min-w-[180px] flex items-start gap-2.5 cursor-pointer text-[13px]">
              <input type="checkbox" checked={tickedReviewed} onChange={e => setTickedReviewed(e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0"/>
              <span>I have read <strong>{policy.policy_code}</strong>{file.version_label && <span className="font-mono text-text-muted"> ({file.version_label})</span>} in full and understand my obligations.</span>
            </label>
          )}
          <Button
            disabled={!canAcknowledge}
            icon={canAcknowledge ? CheckCircle2 : Lock}
            onClick={submitAck}
          >
            {acking ? <Spinner size={14}/> : "Acknowledge"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}

// If pdf.js fails to produce a page within a generous window after the bytes
// arrived, flag the reader as broken so the dwell-only gate takes over. The
// PdfReader shows its own error text; this just unlocks the bar.
function ReaderHealth({ bytes, onBroken }: { bytes: ArrayBuffer | null; onBroken: () => void }) {
  useEffect(() => {
    if (!bytes) return;
    const id = setTimeout(() => {
      const anyCanvas = document.querySelector("canvas[aria-label^='Page 1 of']") as HTMLCanvasElement | null;
      if (!anyCanvas || anyCanvas.width === 0) onBroken();
    }, 20000);
    return () => clearTimeout(id);
  }, [bytes, onBroken]);
  return null;
}

// -----------------------------------------------------------------------------
// State 1: already acknowledged this exact version, ack still valid.

function AlreadyAckedView({ policy, file, ack }: {
  policy: PolicyDetail; file: FileDetail; ack: AckSnapshot;
}) {
  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <BackLink/>
      <header className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[13px] text-accent">{policy.policy_code}</span>
          {file.version_label && (
            <span className="font-mono text-[12px] text-text-dim">{file.version_label}</span>
          )}
          <Pill label="acknowledged" tone="ok" />
        </div>
        <h1 className="text-[22px] font-semibold tracking-tight mt-1">{policy.display_name}</h1>
      </header>
      <Card>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-ok">
            <Shield size={20}/>
            <span className="font-semibold">You're all set.</span>
          </div>
          <div className="text-[13px] text-text-muted">
            You acknowledged this policy on{" "}
            <strong className="text-text">{ack.last_ack_at ? fmtDate(ack.last_ack_at) : "—"}</strong>.{" "}
            Your acknowledgement is valid until{" "}
            <strong className="text-text">{ack.last_ack_expires_at ? fmtDate(ack.last_ack_expires_at) : "—"}</strong>.
          </div>
          <div className="text-[12px] text-text-muted">
            You'll be asked to re-acknowledge if the policy is revised, or once every 12 months —
            whichever comes first. No action needed right now.
          </div>
          <div className="flex justify-end">
            <Link to="/policies">
              <Button variant="secondary">Back to My Policies</Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------

function BackLink() {
  return (
    <Link to="/policies" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
      <ArrowLeft size={14}/> My Policies
    </Link>
  );
}
